import { useEffect, useState } from "react";
import { INSIGHT_RANGES } from "../../../../shared/goals/insights";
import { ccApi, type Dates, type GoalNumbers, type HomeGoal } from "../../../lib/ccApi";
import { datesOf, isoDay, paceLine, type PaceLine } from "../v2";

/** Home's numbers and words, shared by the computer's Command Center and the phone's Home. */

/** Each goal's numbers over the dates picked, read from the stored daily results. */
export function useNumbers(dates: Dates): Map<string, GoalNumbers> {
  const [nums, setNums] = useState<Map<string, GoalNumbers>>(new Map());
  const r = datesOf(dates, null);
  const from = r ? isoDay(r[0]) : "0000-01-01";
  const to = r ? isoDay(r[1]) : isoDay(new Date());
  useEffect(() => {
    let live = true;
    const read = () =>
      ccApi.homeNumbers(from, to).then(
        (x) => live && setNums(new Map(Object.entries(x.goals))),
        () => undefined,
      );
    void read();
    const t = setInterval(() => document.visibilityState === "visible" && void read(), 60_000);
    return () => {
      live = false;
      clearInterval(t);
    };
  }, [from, to]);
  return nums;
}

export const running = (g: HomeGoal) => g.state === "live" || g.state === "paused";

export function paceOf(g: HomeGoal, n: GoalNumbers | undefined): PaceLine | null {
  if (!g.result || !(running(g) || g.state === "done")) return null;
  return paceLine({ totals: { results: g.results?.results ?? 0 }, days: n?.recent ?? [] }, g.result, g.launchedAt, g.state === "done");
}

/** How the goals stand: running, on pace, behind, getting ready, finished. */
export function standing(goals: HomeGoal[], nums: Map<string, GoalNumbers>) {
  const run = goals.filter(running);
  const paces = run.map((g) => paceOf(g, nums.get(g.id)));
  return {
    run: run.length,
    on: paces.filter((p) => p?.kind === "on").length,
    behind: paces.filter((p) => p?.kind === "behind").length,
    ready: goals.filter((g) => g.state === "getting_ready").length,
    done: goals.filter((g) => g.state === "done").length,
  };
}

/** The money and results over the dates picked, across `goals`. */
export function totals(goals: HomeGoal[], nums: Map<string, GoalNumbers>) {
  return {
    spent: goals.reduce((t, g) => t + (nums.get(g.id)?.spendCents ?? 0), 0),
    results: goals.reduce((t, g) => t + (nums.get(g.id)?.results ?? 0), 0),
  };
}

/** The dates' own name: "Last 7 days", or "Custom" for days picked by hand. */
export const rangeLabel = (d: Dates) => ("preset" in d ? (INSIGHT_RANGES.find(([k]) => k === d.preset)?.[1] ?? "") : "");

export const greeting = (d = new Date()) => (d.getHours() < 12 ? "Good morning" : d.getHours() < 18 ? "Good afternoon" : "Good evening");
export const longToday = () => new Date().toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" });
